import React from "react";
import api from "@/lib/api";
import {
  CaretDown, MagnifyingGlass, Plus, ClockCounterClockwise,
  WarningCircle, Paperclip, X, FilePdf, FileXls
} from "@phosphor-icons/react";

/**
 * Per-agent gap-fill wizard.
 *
 * Dependency chain:
 *   business_unit / procurement_bu → clears + re-fetches supplier
 *   supplier                       → clears + re-fetches supplier_site
 *
 * KEY FIX: values stores the numeric ID (row.value).
 * A separate labelCache stores {id → label string} so the selected label
 * always renders correctly even after the dropdown closes and rows resets.
 *
 * NOTE: fields that resolve to a friendly display name (lookups, static
 * selects) also store `${field.name}__label` in values, so consumers
 * (e.g. AgentRunModal building a chat-context string for chat-only agents)
 * can show "Vision Operations" instead of a raw ID/code without re-fetching.
 *
 * PROCUREMENT BU ID: the BU LOV returns the Oracle ProcurementBUId in each
 * row's `number`. The set() helper persists it as `procurement_bu_id` (in the
 * SAME onChange that sets the name — no stale-closure race) so the RFP create
 * payload always carries the id, not just the name.
 */

const fmtSub = (r) => (r.sub ? ` · ${r.sub}` : "");

// Which field provides the filter param for a dependent lookup
const LOOKUP_DEPENDS = {
  suppliers:      { paramName: "procurement_bu_id", parentField: ["business_unit", "procurement_bu"] },
  supplier_sites: { paramName: "supplier_id",       parentField: ["supplier"] },
};

// Downstream fields to clear when a parent changes
const CASCADE_CLEAR = {
  business_unit:  ["supplier", "supplier_site"],
  procurement_bu: ["supplier", "supplier_site"],
  supplier:       ["supplier_site"],
};

/* Shared field-shell styling. One place to tune the whole wizard's look. */
const shell = ({ missing, filled, disabled }) => {
  const base =
    "flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors " +
    "border-l-[3px]";
  if (disabled)
    return `${base} bg-gray-50 border-gray-200 border-l-gray-200 cursor-not-allowed`;
  if (missing)
    return `${base} bg-rose-50/40 border-rose-200 border-l-rose-400 focus-within:border-rose-400`;
  if (filled)
    return `${base} bg-white border-gray-200 border-l-[#0020C2] focus-within:border-[#0020C2] cursor-text`;
  return `${base} bg-white border-gray-200 border-l-gray-200 hover:border-gray-300 focus-within:border-[#0020C2] focus-within:border-l-[#0020C2] cursor-text`;
};

/* ── Ageing BU field — search-as-you-type + scroll pagination (25/page) ──────
   Used when field.lookup === "procurement_business_units" (RFP Procurement BU).
   Hits /erp-agents/rfp/lov/procurement-business-units, which caps limit, so it
   pages via offset + has_more. Stores the BU NAME as the field value AND passes
   the BU id (row.number) as a 3rd onChange arg so it persists as
   procurement_bu_id. */
function AgeingBUField({ field, value, onChange, missing }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const boxRef = React.useRef(null);
  const listRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const reqIdRef = React.useRef(0);

  const PAGE = 25;
  const PATH = "/erp-agents/rfp/lov/procurement-business-units";

  const fetchPage = React.useCallback(async (search, nextOffset, append) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(PATH, {
        params: { search, offset: nextOffset, limit: PAGE },
      });
      if (myReq !== reqIdRef.current) return; // ignore stale
      const newItems = data?.items || [];
      setItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setHasMore(!!data?.has_more);
      setOffset(nextOffset + newItems.length);
      if (data?.error) setErr("Couldn't load — check the Oracle profile.");
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      setErr(e?.response?.data?.detail || e.message || "Load failed");
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, []);

  // Load first page + debounced search whenever the dropdown is open.
  React.useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      fetchPage(q.trim(), 0, false);
      if (listRef.current) listRef.current.scrollTop = 0;
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, open, fetchPage]);

  // Close on outside click.
  React.useEffect(() => {
    const onDoc = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const onScroll = (e) => {
    const el = e.target;
    if (!loading && hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      fetchPage(q.trim(), offset, true);
    }
  };

  const pick = (name, number) => {
    // Set BU name AND its id together via a single onChange (3rd arg = BU id),
    // so procurement_bu_id is persisted without a stale-closure race.
    onChange(name, name, number);
    setOpen(false);
    setQ("");
  };

  const filled = !!value;

  return (
    <div ref={boxRef} className="relative" data-testid={`wizard-field-${field.name}`}>
      <div
        className={shell({ missing, filled, disabled: false })}
        onClick={() => setOpen(true)}
      >
        <MagnifyingGlass size={14} weight="bold" className="text-gray-400 shrink-0" />
        <input
          data-testid={`wizard-input-${field.name}`}
          value={open ? q : (value || "")}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={field.placeholder || `Search ${field.label}…`}
          title={!open && value ? value : undefined}
          className={`flex-1 min-w-0 outline-none bg-transparent font-mono text-[13px] truncate ${filled ? "text-gray-700" : "text-gray-900"}`}
        />
        {value && (
          <button
            onClick={(e) => { e.stopPropagation(); onChange("", "", ""); }}
            className="text-gray-300 hover:text-gray-600 shrink-0 transition-colors"
            data-testid={`wizard-clear-${field.name}`}
            title="Clear"
          >
            <X size={12} weight="bold" />
          </button>
        )}
        <CaretDown size={12} weight="bold" className={`shrink-0 transition-transform ${open ? "rotate-180" : ""} text-gray-400`} />
      </div>

      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1.5 rounded-lg border border-gray-200 bg-white shadow-xl shadow-gray-200/60 overflow-hidden"
          data-testid={`wizard-dropdown-${field.name}`}
        >
          <div ref={listRef} onScroll={onScroll} className="max-h-72 overflow-y-auto">
            {err && <div className="px-3 py-2 text-xs text-red-600">{err}</div>}
            {!err && items.length === 0 && !loading && (
              <div className="px-3 py-3 text-xs text-gray-400">
                No business units found{q.trim() ? ` for "${q.trim()}"` : ""}.
              </div>
            )}
            {items.map((it, i) => {
              const active = String(it.name) === String(value);
              return (
                <button
                  key={`${it.name}-${i}`}
                  onClick={() => pick(it.name, it.number)}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-gray-50 last:border-0 transition-colors ${active ? "bg-[#0020C2]/5" : "hover:bg-gray-50"}`}>
                  <div className={`font-medium ${active ? "text-[#0020C2]" : "text-gray-900"}`}>{it.name}</div>
                  {it.number && <div className="text-[10px] font-mono text-gray-500">#{it.number}</div>}
                </button>
              );
            })}
            {loading && (
              <div className="px-3 py-2 text-xs text-gray-400 flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
                Loading…
              </div>
            )}
            {!loading && hasMore && (
              <div className="px-3 py-1.5 text-[10px] text-gray-400 text-center">Scroll for more…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LookupField({ field, value, onChange, missing, onCreateNew, lookupSpecs, allValues, onDependencyChange, labelCache, setLabelCache, fixedParams }) {
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const inputRef = React.useRef();
  const create_agent_key = lookupSpecs?.[field.lookup]?.create_agent;

  // Build extra params for dependent lookups
  const extraParams = React.useMemo(() => {
    const dep = LOOKUP_DEPENDS[field.lookup];
    if (!dep) return {};
    for (const pf of dep.parentField) {
      const parentVal = allValues?.[pf];
      if (parentVal) {
        return { [dep.paramName]: String(parentVal) };
      }
    }
    return {};
  }, [field.lookup, allValues, field.name]);

  // Is this field locked until a parent is selected?
  const waitingForParent = React.useMemo(() => {
    const dep = LOOKUP_DEPENDS[field.lookup];
    if (!dep) return false;
    return dep.parentField.every((pf) => !allValues?.[pf]);
  }, [field.lookup, allValues]);

  const fetchRows = React.useCallback(async (term) => {
    if (waitingForParent) { setRows([]); return; }
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/erp-agents/lookups/${field.lookup}`, {
        params: { q: term || "", limit: 20, ...extraParams, ...fixedParams },
      });
      setRows(data.rows || []);
      // Cache labels so selected value always renders correctly after dropdown closes
      if (data.rows?.length) {
        setLabelCache((prev) => {
          const next = { ...prev };
          data.rows.forEach((r) => { if (r.value != null) next[String(r.value)] = r; });
          return next;
        });
      }
      if (data.status !== "ok") {
        setErr(
          data.status === "auth_error"    ? "We couldn't sign in to Oracle. Please check your Oracle username and password on the profile page." :
          data.status === "unconfigured"  ? "Please connect your Oracle identity first so we can load these options." :
          data.status === "network_error" ? "We couldn't reach Oracle right now. Please check your connection and try again." :
          data.status === "oracle_error"  ? "Oracle couldn't return these options right now. Please try again in a moment." :
          data.source === "stale_cache"   ? "Oracle is unavailable — showing the last saved options." : ""
        );
      }
    } catch (e) {
      setErr("Something went wrong loading these options. Please try again.");
      setRows([]);
    } finally { setLoading(false); }
  }, [field.lookup, waitingForParent, extraParams, setLabelCache, fixedParams]);

  // Fetch when dropdown opens or query changes
  React.useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => fetchRows(q), 200);
    return () => clearTimeout(id);
  }, [open, q, fetchRows]);

  // Auto-fetch first page when parent value changes (even if dropdown is closed)
  // so the supplier list is ready the moment the user clicks the field
  const extraParamsKey = JSON.stringify(extraParams);
  React.useEffect(() => {
    if (waitingForParent || Object.keys(extraParams).length === 0) return;
    fetchRows("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraParamsKey]);

  // Pre-fetch parentless LOVs (Procurement BU, Business Unit, Currency, etc.) once
  // on mount, so an API call fires immediately on page load and the dropdown is
  // already populated — instead of waiting for the user to open it.
  React.useEffect(() => {
    if (!field.lookup) return;
    if (LOOKUP_DEPENDS[field.lookup]) return; // dependent lookups load via the effect above
    fetchRows("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.lookup]);

  // Close on outside click
  React.useEffect(() => {
    const onDoc = (e) => { if (!inputRef.current?.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (row) => {
    // Store the NAME (row.value) directly — Oracle create-supplier payload needs the name.
    // Also pass row.label so consumers building a display/context string get a
    // friendly name even when row.value is an ID rather than a name.
    onChange(row.value, row.label);
    onDependencyChange?.(field.name, row.value);
    setQ("");
    setOpen(false);
  };

  // Resolve display label: labelCache → rows → raw value
  const shownLabel = (() => {
    if (!value) return "";
    const cached = labelCache[String(value)];
    if (cached) return `${cached.label}${fmtSub(cached)}`;
    const inRows = rows.find((r) => String(r.value) === String(value));
    if (inRows) return `${inRows.label}${fmtSub(inRows)}`;
    return String(value);  // fallback: show raw ID
  })();

  const placeholder = waitingForParent
    ? `Select ${LOOKUP_DEPENDS[field.lookup]?.parentField[0]?.replace(/_/g, " ")} first…`
    : (field.placeholder || `Search ${field.label}…`);

  const filled = !!value && !waitingForParent;

  return (
    <div ref={inputRef} className="relative" data-testid={`wizard-field-${field.name}`}>
      <div
        className={shell({ missing, filled, disabled: waitingForParent })}
        onClick={() => { if (!waitingForParent) setOpen(true); }}
      >
        <MagnifyingGlass size={14} weight="bold" className={waitingForParent ? "text-gray-300 shrink-0" : "text-gray-400 shrink-0"} />
        <input
          data-testid={`wizard-input-${field.name}`}
          value={open ? q : shownLabel}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { if (!waitingForParent) setOpen(true); }}
          placeholder={placeholder}
          disabled={waitingForParent}
          title={!open && shownLabel ? shownLabel : undefined}
          className={`flex-1 min-w-0 outline-none bg-transparent font-mono text-[13px] truncate disabled:cursor-not-allowed disabled:text-gray-400 ${filled ? "text-gray-700" : "text-gray-900"}`}
        />
        {value && !waitingForParent && (
          <button
            onClick={(e) => { e.stopPropagation(); onChange("", ""); onDependencyChange?.(field.name, ""); }}
            className="text-gray-300 hover:text-gray-600 shrink-0 transition-colors"
            data-testid={`wizard-clear-${field.name}`}
            title="Clear"
          >
            <X size={12} weight="bold" />
          </button>
        )}
        <CaretDown size={12} weight="bold" className={`shrink-0 transition-transform ${open ? "rotate-180" : ""} text-gray-400`} />
      </div>

      {/* Recent chips — only shown when empty and dropdown closed */}
      {!open && !value && !waitingForParent && (field.recent || []).length > 0 && (
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid={`wizard-recent-${field.name}`}>
          <ClockCounterClockwise size={10} weight="bold" className="text-gray-400" />
          {field.recent.map((r) => (
            <button key={r.value}
              onClick={() => { onChange(r.value, r.label); onDependencyChange?.(field.name, r.value); }}
              className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-gray-200 text-gray-600 hover:border-[#0020C2] hover:text-[#0020C2] transition-colors">
              {r.label}
            </button>
          ))}
        </div>
      )}

      {open && !waitingForParent && (
        <div
          className="absolute z-50 left-0 right-0 mt-1.5 rounded-lg border border-gray-200 bg-white shadow-xl shadow-gray-200/60 max-h-72 overflow-y-auto"
          data-testid={`wizard-dropdown-${field.name}`}
        >
          {loading && <div className="px-3 py-2.5 text-xs text-gray-500 flex items-center gap-2">
            <span className="w-3 h-3 border-2 border-gray-200 border-t-[#0020C2] rounded-full animate-spin" />
            Searching Oracle…
          </div>}
          {!loading && err && (
            <div className="px-3 py-2 text-xs border-b border-gray-100 bg-amber-50 text-amber-800 flex items-start gap-1.5">
              <WarningCircle size={12} weight="fill" className="mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}
          {!loading && rows.length === 0 && !err && (
            <div className="px-3 py-3 text-xs text-gray-500">
              No matches{q ? ` for "${q}"` : ""}.
              {create_agent_key && (
                <button onClick={() => onCreateNew?.(create_agent_key)}
                  data-testid={`wizard-create-new-${field.name}`}
                  className="ml-2 inline-flex items-center gap-1 font-bold text-[#0020C2] hover:underline">
                  <Plus size={11} weight="bold" /> Create new
                </button>
              )}
            </div>
          )}
          {!loading && rows.map((r) => {
            const active = String(r.value) === String(value);
            return (
              <button key={r.value}
                data-testid={`wizard-option-${field.name}-${r.value}`}
                onClick={() => pick(r)}
                className={`w-full text-left px-3 py-2 text-sm border-b border-gray-50 last:border-b-0 transition-colors ${active ? "bg-[#0020C2]/5" : "hover:bg-gray-50"}`}>
                <div className={`font-medium ${active ? "text-[#0020C2]" : "text-gray-900"}`}>{r.label}</div>
                {r.sub && <div className="text-[10px] font-mono text-gray-500">{r.sub}</div>}
              </button>
            );
          })}
          {create_agent_key && rows.length > 0 && (
            <button onClick={() => onCreateNew?.(create_agent_key)}
              data-testid={`wizard-create-new-${field.name}`}
              className="w-full text-left px-3 py-2 text-xs font-bold text-[#0020C2] hover:bg-[#0020C2]/5 inline-flex items-center gap-1 border-t border-gray-100">
              <Plus size={11} weight="bold" /> Create new {field.label.toLowerCase()}…
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FileField({ field, value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const [err, setErr] = React.useState("");
  const inputRef = React.useRef();

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr("");
    onChange([...items, ...files]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const iconFor = (name) => {
    const n = (name || "").toLowerCase();
    if (n.endsWith(".pdf"))  return <FilePdf  size={14} weight="fill" className="text-[#DC2626]" />;
    if (n.endsWith(".xlsx") || n.endsWith(".xls")) return <FileXls size={14} weight="fill" className="text-emerald-600" />;
    return <Paperclip size={14} className="text-gray-500" />;
  };

  return (
    <div data-testid={`wizard-file-${field.name}`}>
      <label className="rounded-lg border border-dashed border-gray-300 px-3 py-3 flex items-center gap-2 cursor-pointer hover:border-[#0020C2] hover:bg-[#0020C2]/5 transition-colors text-xs">
        <Paperclip size={14} weight="bold" className="text-[#0020C2]" />
        <span className="font-medium text-gray-700">Click to upload PDF / Excel files</span>
        <input ref={inputRef} type="file" multiple={field.multiple !== false}
          accept={field.accept || ".pdf,.xlsx,.xls"}
          onChange={onPick}
          data-testid={`wizard-file-input-${field.name}`}
          className="hidden" />
      </label>
      {err && <div className="text-[11px] text-[#DC2626] mt-1">{err}</div>}
      {items.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid={`wizard-file-list-${field.name}`}>
          {items.map((it, i) => {
            const name = it.file_name || it.name;
            const size = it.size || 0;
            return (
              <li key={`${name}-${size}-${i}`} className="flex items-center gap-2 rounded-md border border-gray-200 px-2 py-1.5 text-xs bg-white">
                {iconFor(name)}
                <span className="flex-1 truncate font-mono">{name}</span>
                <span className="text-[10px] font-mono text-gray-400 shrink-0">{Math.ceil(size / 1024)} KB</span>
                <button onClick={() => remove(i)} data-testid={`wizard-file-remove-${field.name}-${i}`}
                  className="text-gray-400 hover:text-[#DC2626] transition-colors"><X size={12} weight="bold" /></button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function MultiSelectField({ field, value, onChange, missing }) {
  const options = field.static_options || [];
  const selected = Array.isArray(value) ? value : (value ? [value] : []);

  const toggle = (optValue) => {
    const next = selected.includes(optValue)
      ? selected.filter((v) => v !== optValue)
      : [...selected, optValue];
    onChange(next);
  };

  return (
    <div
      data-testid={`wizard-multiselect-${field.name}`}
      className={`rounded-lg border border-l-[3px] max-h-40 overflow-y-auto p-2 space-y-1 bg-white ${
        missing ? "border-rose-200 border-l-rose-400 bg-rose-50/40" : selected.length ? "border-gray-200 border-l-[#0020C2]" : "border-gray-200 border-l-gray-200"
      }`}
    >
      {options.map((o) => (
        <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1.5 py-1 rounded">
          <input
            type="checkbox"
            checked={selected.includes(o.value)}
            onChange={() => toggle(o.value)}
            className="accent-[#0020C2]"
          />
          {o.label}
        </label>
      ))}
    </div>
  );
}

function ClassificationDropdown({ field, value, onChange, missing }) {
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef();
  const options = field.static_options || [];
  const filtered = q ? options.filter(o => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  const shownLabel = options.find(o => o.value === value)?.label || value || "";
  const filled = !!value;

  React.useEffect(() => {
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative" data-testid={`wizard-field-${field.name}`}>
      <div
        onClick={() => setOpen(v => !v)}
        className={`${shell({ missing, filled, disabled: false })} cursor-pointer`}
      >
        <MagnifyingGlass size={14} weight="bold" className="text-gray-400 shrink-0" />
        <span
          title={shownLabel || undefined}
          className={`flex-1 min-w-0 truncate text-[13px] ${shownLabel ? (filled ? "text-gray-700" : "text-gray-900") : "text-gray-400"}`}>
          {shownLabel || `Select ${field.label}…`}
        </span>
        {value && (
          <button onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="text-gray-300 hover:text-gray-600 shrink-0 transition-colors">
            <X size={12} weight="bold" />
          </button>
        )}
        <CaretDown size={12} weight="bold" className={`text-gray-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1.5 rounded-lg border border-gray-200 bg-white shadow-xl shadow-gray-200/60 max-h-60 overflow-hidden flex flex-col">
          <div className="border-b border-gray-100 px-3 py-2">
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search classification…"
              className="w-full text-xs outline-none"
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            <div
              onClick={() => { onChange(""); setOpen(false); }}
              className="px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
            >
              — None —
            </div>
            {filtered.map(opt => (
              <div
                key={opt.value}
                onClick={() => { onChange(opt.value, opt.label); setOpen(false); setQ(""); }}
                className={`px-3 py-2 text-sm cursor-pointer border-b border-gray-50 last:border-0 ${
                  value === opt.value ? "bg-[#0020C2]/5 text-[#0020C2] font-semibold" : "hover:bg-gray-50 text-gray-900"
                }`}
              >
                {opt.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BankAccountSubForm({ values, onChange }) {
  const [expanded, setExpanded] = React.useState(false);
  const hasData = values.bank_account_number || values.bank_name;

  return (
    <div className="mt-2 rounded-lg border border-gray-200 overflow-hidden" data-testid="bank-account-subform">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <Plus size={11} weight="bold" className="text-[#0020C2]" />
          Bank Account Details
          {hasData && <span className="text-[#0020C2] ml-1 font-semibold">· filled</span>}
        </span>
        <CaretDown size={11} weight="bold" className={`text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="p-3 grid grid-cols-2 gap-2 bg-white">
          {[
            { key: "bank_account_number", label: "Account Number", placeholder: "e.g. AE040030010598420124001" },
            { key: "bank_name",           label: "Bank Name",      placeholder: "e.g. Abu Dhabi Commercial Bank" },
            { key: "bank_branch",         label: "Bank Branch",    placeholder: "e.g. Main Branch" },
          ].map(({ key, label, placeholder }) => (
            <div key={key} className={key === "bank_account_number" ? "col-span-2" : ""}>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">{label}</label>
              <input
                type="text"
                value={values[key] || ""}
                onChange={e => onChange({ ...values, [key]: e.target.value })}
                placeholder={placeholder}
                className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-[#0020C2]"
              />
            </div>
          ))}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Country</label>
            <select
              value={values.bank_account_country || ""}
              onChange={e => onChange({ ...values, bank_account_country: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-[#0020C2]"
            >
              <option value="">— Select country —</option>
              {[["AE","UAE"],["IN","India"],["US","USA"],["GB","UK"],["SA","Saudi Arabia"],["QA","Qatar"],["KW","Kuwait"]]
                .map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Currency</label>
            <select
              value={values.bank_currency || ""}
              onChange={e => onChange({ ...values, bank_currency: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-[#0020C2]"
            >
              <option value="">— Select currency —</option>
              {["AED","USD","EUR","GBP","SAR","QAR","KWD","INR"].map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2 text-[10px] text-gray-500 bg-[#0020C2]/5 rounded-md px-2 py-1.5">
            These details will be added as the supplier bank account under Account Owner after supplier creation.
          </div>
        </div>
      )}
    </div>
  );
}

function StaticSelectField({ field, value, onChange, missing }) {
  const options = field.static_options || [];
  const filled = !!value;

  const handleChange = (e) => {
    const v = e.target.value;
    const label = options.find((o) => o.value === v)?.label;
    onChange(v, label);
  };

  return (
    <select
      data-testid={`wizard-dropdown-${field.name}`}
      value={value || ""}
      onChange={handleChange}
      className={`w-full rounded-lg border border-l-[3px] px-3 py-2.5 text-sm bg-white cursor-pointer appearance-none bg-no-repeat transition-colors focus:outline-none focus:border-[#0020C2] ${
        missing
          ? "border-rose-200 border-l-rose-400 bg-rose-50/40"
          : filled
            ? "border-gray-200 border-l-[#0020C2] text-gray-700"
            : "border-gray-200 border-l-gray-200 hover:border-gray-300 text-gray-900"
      }`}
      style={{
        backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
        backgroundPosition: "right 10px center",
        backgroundSize: "16px",
        paddingRight: "32px",
      }}
    >
      <option value="" disabled>
        {`-- Select ${field.label} --`}
      </option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function PlainField({ field, value, onChange, missing }) {
  const type = field.type === "number" ? "number" : (field.type === "date" ? "date" : "text");
  const filled = value !== undefined && value !== null && String(value).length > 0;
  const inputClass = `w-full rounded-lg border border-l-[3px] px-3 py-2.5 text-sm font-mono transition-colors focus:outline-none focus:border-[#0020C2] focus:border-l-[#0020C2] ${
    missing
      ? "border-rose-200 border-l-rose-400 bg-rose-50/40"
      : filled
        ? "border-gray-200 border-l-[#0020C2] text-gray-700"
        : "border-gray-200 border-l-gray-200 hover:border-gray-300"
  }`;

  // Today (local time), YYYY-MM-DD — used as the min for date fields so past
  // dates can't be picked, and to coerce any empty/past date up to today.
  const todayStr = React.useMemo(() => {
    const d = new Date();
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
  }, []);

  // If a date field arrives empty or with a past value (e.g. a stale default
  // like 2026-07-17), snap it to today so Oracle's "open date must be after
  // today" rule is satisfied and the user sees a valid date.
  React.useEffect(() => {
    if (field.type !== "date") return;
    if (!value || String(value) < todayStr) {
      onChange(todayStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.type]);

  if (field.type === "textarea") {
    return (
      <textarea data-testid={`wizard-input-${field.name}`}
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        rows={4} placeholder={field.placeholder} className={inputClass} />
    );
  }

  return (
    <input
      type={type}
      data-testid={`wizard-input-${field.name}`}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      min={field.type === "date" ? todayStr : undefined}
      className={inputClass}
    />
  );
}

export default function WizardFields({
  agentKey, values, onChange, missingFields, onCreateNew,
  fieldErrors = {}, fieldWarnings = {}, onSchemaLoad, stacked = false,
}) {
  const [schema, setSchema] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [labelCache, setLabelCache] = React.useState({});

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSchema(null);
    setLabelCache({});
    api.get(`/erp-agents/agent-fields/${agentKey}`)
      .then(({ data }) => {
        if (!cancelled) {
          setSchema(data);
          onSchemaLoad?.((data?.fields || []).filter((f) => !f.hidden));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSchema({ fields: [] });
          onSchemaLoad?.([]);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agentKey]);

  React.useEffect(() => {
    if (!schema?.fields) return;
    const seed = {};
    for (const f of schema.fields) {
      if (!values[f.name] && f.tenant_default) seed[f.name] = f.tenant_default;
    }
    if (Object.keys(seed).length) onChange({ ...values, ...seed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema]);

  if (loading) return <div className="text-xs text-gray-500" data-testid="wizard-loading">Loading wizard…</div>;
  if (!schema || !schema.fields || schema.fields.length === 0) return null;

  const missingSet = new Set(missingFields || []);
  const set = (name) => (v, label, extraId) => {
    const next = { ...values, [name]: v };
    if (label !== undefined) next[`${name}__label`] = label;
    // The Procurement BU field passes its Oracle ProcurementBUId as a 3rd arg.
    // Persist it as procurement_bu_id in the SAME onChange (no stale-closure
    // race) so the RFP create payload always carries the id, not just the name.
    if (name === "procurement_bu") {
      if (extraId !== undefined && extraId !== "") {
        next["procurement_bu_id"] = extraId;
      } else if (v === "" || v === null || v === undefined) {
        // BU cleared → drop the stale id too.
        next["procurement_bu_id"] = "";
      }
    }
    onChange(next);
  };

  const handleDependencyChange = (changedField, newValue) => {
    const toClear = CASCADE_CLEAR[changedField] || [];
    if (toClear.length === 0) return;
    const next = { ...values, [changedField]: newValue };
    toClear.forEach((f) => { next[f] = ""; next[`${f}__label`] = ""; });
    onChange(next);
  };

  const visibleFields = schema.fields.filter((f) => !f.hidden);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3" data-testid="wizard-fields">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400">
        Guided wizard · {visibleFields.length} fields
      </div>
      <div className={stacked ? "grid grid-cols-1 gap-y-3.5" : "grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-3.5"}>
        {visibleFields.map((f) => {
          const err  = fieldErrors[f.name];
          const warn = fieldWarnings[f.name];
          const flagMissing = missingSet.has(f.name) || !!err;
          return (
            <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 block mb-1.5">
                {f.label}
                {f.required && <span className="text-rose-400 ml-0.5">*</span>}
              </label>
              {f.type === "file"
                ? <FileField field={f} value={values[f.name] || []} onChange={set(f.name)} />
                : f.name === "business_classifications"
                  ? <ClassificationDropdown field={f} value={values[f.name] || ""} onChange={set(f.name)} missing={flagMissing} />
                  : f.static_options && f.multi
                  ? <MultiSelectField field={f} value={values[f.name] || []} onChange={set(f.name)} missing={flagMissing} />
                : f.static_options
                  ? <StaticSelectField field={f} value={values[f.name] || ""} onChange={set(f.name)} missing={flagMissing} />
                : f.lookup === "procurement_business_units"
                  ? <AgeingBUField field={f} value={values[f.name] || ""} onChange={set(f.name)} missing={flagMissing} />
                : f.lookup
                  ? <LookupField
                      field={f}
                      value={values[f.name] || ""}
                      onChange={set(f.name)}
                      missing={flagMissing}
                      onCreateNew={onCreateNew}
                      lookupSpecs={schema.lookup_specs}
                      allValues={values}
                      onDependencyChange={handleDependencyChange}
                      labelCache={labelCache}
                      setLabelCache={setLabelCache}
                    />
                  : <PlainField field={f} value={values[f.name] || ""} onChange={set(f.name)} missing={flagMissing} />
              }
              {f.name === "payment_method" && values[f.name] && (
                <BankAccountSubForm values={values} onChange={onChange} />
              )}
              {err && (
                <div data-testid={`wizard-error-${f.name}`} className="text-[11px] text-rose-600 mt-1 flex items-start gap-1">
                  <WarningCircle size={11} weight="fill" className="mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
              {!err && warn && (
                <div data-testid={`wizard-warning-${f.name}`} className="text-[11px] text-amber-700 mt-1 flex items-start gap-1">
                  <WarningCircle size={11} weight="fill" className="mt-0.5 shrink-0" />
                  <span>{warn}</span>
                </div>
              )}
              {f.name === "business_classifications" && (values[f.name] || []).length > 0 && (() => {
                const map = schema.classification_attachment_map || {};
                const required = (values[f.name] || []).filter((c) => map[c]).map((c) => map[c]);
                if (!required.length) return null;
                return (
                  <div data-testid="required-attachment-banner" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                    <p className="font-bold mb-1">Required Document{required.length > 1 ? "s" : ""}:</p>
                    <ul className="list-disc list-inside">
                      {required.map((doc) => <li key={doc}>{doc}</li>)}
                    </ul>
                    <p className="mt-1">Please upload {required.length > 1 ? "these documents" : "this document"} below before submitting.</p>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { LookupField };